import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { App } from '@vendasta/embeddings';
import { isSameApp } from '../ai-knowledge-util';
import { AiKnowledgeService } from '../services/ai-knowledge.service';

import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AiKnowledgeI18nModule } from '../assets/i18n/ai-knowledge-i18n.module';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { ApplicationListComponent } from '../app-list/application-list.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'ai-knowledge-application-selector',
  imports: [
    TranslateModule,
    AiKnowledgeI18nModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    FormsModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    GalaxyLoadingSpinnerModule,
    GalaxyFormFieldModule,
    MatDividerModule,
    MatListModule,
    ApplicationListComponent,
    GalaxyEmptyStateModule
],
  providers: [AiKnowledgeService],
  templateUrl: './application-selector.component.html',
  styleUrls: ['./application-selector.component.scss'],
})
export class ApplicationSelectorComponent implements OnInit {
  @Input() knowledgeSourceId!: string;
  @Output() createWidgetClicked = new EventEmitter<boolean>();
  hasUnsavedChanges = false;
  forceSubmit = false;

  private readonly knowledgeService = inject(AiKnowledgeService);
  private confirmationModal = inject(OpenConfirmationModalService);
  private readonly translateService = inject(TranslateService);
  destroyRef = inject(DestroyRef);
  apps = signal<App[]>([]);
  searchValue = signal<string>('');
  selectedApps = signal<App[]>([]);
  filteredApps = computed(() =>
    this.apps().filter(
      (app) =>
        app.name.toLowerCase().indexOf(this.searchValue()) >= 0 &&
        !this.selectedApps().some((selected) => isSameApp(selected, app)),
    ),
  );
  readonly isLoading = signal(true);
  readonly isCreatingWidget = signal(false);

  selectedAppControl = new FormControl();

  @ViewChild('appInput') appInput!: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete!: MatAutocomplete;
  @ViewChild('trigger') matAutocompleteTrigger!: MatAutocompleteTrigger;

  async ngOnInit(): Promise<void> {
    await this.loadApps();

    this.selectedAppControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((searchValue) => {
      if (typeof searchValue === 'string') {
        this.searchValue.set(searchValue?.toLowerCase());
      }
    });
  }

  async loadApps(): Promise<void> {
    this.isLoading.set(true);
    this.apps.set(await this.knowledgeService.listAllApps());
    this.forceSubmit = this.apps()?.length == 0;
    if (this.knowledgeSourceId) {
      this.selectedApps.set(await this.knowledgeService.listAllAppsForKnowledgeSource(this.knowledgeSourceId));
    }
    this.isLoading.set(false);
  }

  isCreateWidgetRouteSet(): boolean {
    return this.knowledgeService.isWebChatCreateRouteSet();
  }

  createWidgetClick(): void {
    this.createWidgetClicked.emit(true);
  }

  removeApp(app: App): void {
    this.selectedApps.update((selectedApps) => {
      const index = selectedApps.indexOf(app);
      if (index >= 0) {
        selectedApps.splice(index, 1);
      }
      return [...selectedApps];
    });
    this.hasUnsavedChanges = true;
  }

  addApp(app: App): void {
    this.selectedApps.update((selectedApps) => {
      const alreadySelected = selectedApps.some((a) => isSameApp(a, app));
      if (!alreadySelected) {
        selectedApps.push(app);
      }
      this.clearInputFields();
      this.matAutocompleteTrigger.closePanel();
      return [...selectedApps];
    });
    this.hasUnsavedChanges = true;
  }

  chooseMatch(event: any): void {
    event.preventDefault();
    if (this.filteredApps().length > 1) {
      // Do not select if there are multiple matches
      return;
    }
    if (this.filteredApps().length === 1) {
      const sourceToAdd = this.filteredApps()[0];
      this.addApp(sourceToAdd);
    }
  }

  clearInputFields(): void {
    if (this.appInput?.nativeElement?.value) {
      this.appInput.nativeElement.value = '';
    }
    this.selectedAppControl.setValue('');
  }

  async setAppsForKnowledgeSource(): Promise<boolean> {
    if (this.apps()?.length == 0) {
      const confirmed = await firstValueFrom(
        this.confirmationModal.openModal({
          title: this.translateService.instant('CREATE_WEB_CHAT_WIDGET.NO_WEB_CHAT_WIDGET.TITLE'),
          message: this.translateService.instant('CREATE_WEB_CHAT_WIDGET.NO_WEB_CHAT_WIDGET.BODY'),
          confirmButtonText: this.translateService.instant('CREATE_WEB_CHAT_WIDGET.NO_WEB_CHAT_WIDGET.YES'),
          cancelButtonText: this.translateService.instant('COMMON.NO'),
        }),
      );
      if (confirmed) {
        this.createWidgetClicked.emit(true);
        this.hasUnsavedChanges = false;
      }
      return !confirmed;
    }
    await this.knowledgeService.setAppsForKnowledgeSource(this.knowledgeSourceId, this.selectedApps());
    this.hasUnsavedChanges = false;
    return true;
  }
}
